// Canonical English text of the Docio Health Privacy Policy.
//
// English-only by design, exactly like the three agreements in this directory: the
// published policy is the English one, and a translated banner on the page tells readers
// in other languages that the English version governs. It therefore lives here as
// structured data rather than in i18n — see the note at the top of types.ts.
//
// Before 2026-08-04 the body was fully translated in
// apps/web-marketing/src/i18n/locales/*/legal.json under `privacy.sections`. Only the page
// chrome (title, intro, language notice) is still translated; the policy text is not.

import type { PrivacyPolicyDocument } from './types';

/**
 * Bumped whenever the published Privacy Policy materially changes; recorded in
 * legal_acceptances alongside terms acceptance at signup.
 *
 * 2026-10-25: disclosed separately optional masked web signup recordings,
 * their 30-day retention and withdrawal controls. Other processing is unchanged.
 *
 * 2026-08-07: disclosed that symptom text is summarised by a third-party AI provider
 * (OpenAI) and exactly what it does and does not receive, and corrected the location
 * section — physician location is collected continuously in the background while
 * "Available", which the previous wording ("not in the background unless explicitly
 * permitted") described too loosely to match the app's Info.plist entitlements.
 */
export const PRIVACY_POLICY_VERSION = '2026-10-25';

export const privacyPolicy: PrivacyPolicyDocument = {
  slug: 'privacy-policy',
  documentKey: 'privacy_policy',
  title: 'Docio Health — Privacy Policy',
  shortTitle: 'Privacy Policy',
  version: PRIVACY_POLICY_VERSION,
  sections: [
    {
      id: 'whoWeAre',
      title: `Who We Are`,
      description: `Docio Health Ltd is a private limited company registered in England and Wales (Company No. 17270960), with its registered office at C/O Unit 4, Kinetica, 13 Ramsgate Street, London E8 2FD, United Kingdom. We are responsible for your account data, the details you give us when you register and use the app. Your clinical data (your symptoms, diagnosis, treatment and medical report) is recorded through the app, but is the responsibility of the physician who attends to you: they decide how it is used and kept, in line with their own professional obligations, separately from the account information we control. If you would like a copy of your consultation, diagnosis or treatment record, this is available to you in the app, and you can also contact the physician who treated you directly. For anything privacy-related, contact us at info@dociohealth.com.`,
    },
    {
      id: 'scope',
      title: `What This Policy Covers`,
      description: `This policy covers the Docio Health app and our marketing website, and should be read alongside the Patient Terms of Service or, if you are a physician, the Platform-Physician Agreement. Medical services are provided by independent physicians — Docio is a technology platform, not a healthcare provider — and your clinical records are completed by the physician who attends to you.`,
    },
    {
      id: 'whatWeCollect',
      title: `What We Collect`,
      items: [
        `Account data: your name, email address, phone number, date of birth, gender and profile photo`,
        `Tokenised payment data, processed by Stripe — we never store your full card details`,
        `As a patient: your device location while you are using the app, to find physicians near you and to confirm the address for your visit`,
        `As a physician: your device location continuously while you are set to Available — including when the app is in the background or closed — so we can match you with nearby patients and show a patient your live position on the way to their visit. A notification stays visible while this is active, and sharing stops as soon as you set yourself unavailable`,
        `Clinical data generated during a consultation: symptoms, diagnoses, treatments and the medical report completed by your physician`,
        `Platform usage data such as bookings, ratings and service history`,
        `For physicians: professional registration, licence, insurance and banking details, and the professional profile (specialisms and other information) shown to patients`,
      ],
    },
    {
      id: 'howWeUseIt',
      title: `How We Use It`,
      items: [
        `To match you with nearby physicians and route your service requests`,
        `To operate bookings, payments and receipts`,
        `To summarise the symptoms you describe, using a third-party AI provider, so your physician can prepare before the visit — the summary is never a diagnosis and never replaces the physician's judgement`,
        `To make your medical report available to you in the app`,
        `To verify physician credentials at onboarding and periodically thereafter`,
        `To handle disputes, refunds and support requests`,
        `To respond to requests you make about your personal data, such as asking to see, correct or delete it`,
        `In anonymised form — from which you can no longer be identified — to train our systems and monitor pricing on the platform. Once your data has been anonymised in this way, it can no longer be linked back to you`,
      ],
    },
    {
      id: 'legalBases',
      title: `Legal Bases for Processing`,
      items: [
        `Your explicit consent for health and location data — you can withdraw it at any time, though this may prevent you from using core functions of the app`,
        `Performance of our contract with you, to deliver the service you request`,
        `Our legitimate interests in fraud prevention, platform security and service improvement`,
        `Compliance with our legal obligations`,
      ],
    },
    {
      id: 'sharing',
      title: `Who We Share It With`,
      items: [
        `The physician attending to you: your name, age, gender, confirmed service location, symptoms and, where available, your photo — never your payment details or contact information without your consent, except where the law requires`,
        `You: basic information about the physician treating you, such as their name and area of specialism, so you can choose who to be seen by`,
        `Stripe, for payment processing`,
        `OpenAI, our AI provider, which receives only the symptom description you write plus your age and gender — never your name, contact details, account identifiers or payment information. OpenAI does not use data sent through its API to train its models`,
        `Hosting, analytics and customer support providers acting as processors under our instruction`,
        `Regulators and law enforcement authorities, where required by law`,
        `We never sell your personal data`,
      ],
    },
    {
      id: 'retention',
      title: `How Long We Keep It`,
      description: `We keep your personal data while your account is active and for six years after it is closed, or longer where the law requires. For physicians, this can mean keeping registration, qualification and insurance records for longer, in line with professional record-keeping rules. Transaction records are kept in anonymised form for accounting and audit purposes.`,
    },
    {
      id: 'internationalTransfers',
      title: `International Transfers`,
      description: `Where your data is transferred outside the United Kingdom, we put appropriate safeguards in place in line with applicable data protection law.`,
    },
    {
      id: 'security',
      title: `How We Protect It`,
      description: `We apply technical and organisational security measures, including encryption in transit and strict access controls. Physicians are independently responsible for any copies of your data they hold outside the app.`,
    },
    {
      id: 'yourRights',
      title: `Your Rights`,
      items: [
        `Access the personal data we hold about you`,
        `Have inaccurate data corrected`,
        `Request erasure of your data`,
        `Restrict or object to processing`,
        `Receive your data in a portable format`,
        `Withdraw your consent at any time`,
        `Complain to the Information Commissioner's Office (ICO) at ico.org.uk`,
      ],
    },
    {
      id: 'cookies',
      title: `Cookies`,
      description: `We use essential cookies to keep you logged in and remember your language preference. With your consent, we also use analytics cookies (PostHog) and advertising cookies (Meta, LinkedIn) to understand how the site is used and to show relevant ads to past visitors. You can accept or reject these at any time via "Cookie settings" in the footer. We ask for your consent again at least every 12 months.`,
    },
    {
      id: 'optionalAppAnalytics',
      title: `Optional App Analytics and Signup Recordings`,
      description: `The app offers optional analytics through PostHog in the EU. If you separately choose to share masked signup recordings, we record layout and interactions on web signup and role-selection screens to improve registration. Form entries and other text are hidden; clinical screens, messages, precise location and account identifiers are excluded. Recordings use random identifiers and have a 30-day retention period; removal can take additional processing time after expiry. You can withdraw either choice in Analytics preferences from sign-in or Settings without affecting your care. Withdrawal stops new collection; requests about recordings already held are handled through our privacy request process.`,
    },
    {
      id: 'children',
      title: `Children`,
      description: `Our service is for adults aged 18 and over, and we do not knowingly process children's data. You may use your account to book for a family member only if you are their legal guardian or they are also 18 or over and have given their consent.`,
    },
    {
      id: 'changes',
      title: `Changes to This Policy`,
      description: `If we make material changes to this policy, we will notify you through the app or by email.`,
    },
    {
      id: 'contact',
      title: `Contact Us`,
      description: `Email us at info@dociohealth.com or support@dociohealth.com with any privacy questions. You can also lodge a complaint with the Information Commissioner's Office (ICO).`,
    },
  ],
};
